/**
 * User service implementation
 *
 * Developed By : Travancore Analytics (2025)
 * Author: Akhil Soman
 * Created on: 09/03/2025
 * Project: Vitromed
 * Revision History:
 * SNo.   | Date        | Author        | Task                | Comments |
 * 1      09/03/2025    Akhil Soman       Creating new File
 */

import api from "./api.ts"; // Import the centralized Axios instance

class UserService {
  /**
   * Fetch all users
   * @param url - API endpoint
   * @returns Promise with the response data
   */
  async getAllUsers(url: string) {
    try {
      const response = await api.get(url);
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  }

  /**
   * Fetch user by email
   * @param url - API endpoint
   * @param email - Email of the user to fetch
   * @returns Promise with the response data
   */
  async getUserDetailsByEmail(url: string) {
    try {
      const response = await api.get(url); // Send email in the request body
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching user by email:", error);
      throw error;
    }
  }

  /**
   * Checks if a user exists by their email
   * @param url - API endpoint to check user existence
   * @param email - Email of the user to check
   * @returns Promise that resolves with the response data if the user exists
   * @throws Error if the API request fails or returns an unexpected status code
   */
  async isUserExists(url: string, email: string) {
    try {
      const response = await api.post(url, { email }); // Send email in the request body
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching user by email:", error);
      throw error;
    }
  }

  /**
   * Fetch user by email
   * @param url - API endpoint
   * @param email - Email of the user to fetch
   * @returns Promise with the response data
   */
  async updateUserDetailsById(url: string, data) {
    try {
      const response = await api.put(url, data); // Send email in the request body
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching user by email:", error);
      throw error;
    }
  }
}

const userService = new UserService();
export default userService;
